import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { Manager, ManagerInput } from '@jobos/shared/types/manager';
import { useToast } from '@/hooks/use-toast';
import { clientKeys } from './useClients';

interface ManagersResponse {
  managers: Manager[];
}

interface ManagerDetailResponse {
  manager: Manager;
}

// Query keys
export const managerKeys = {
  all: ['managers'] as const,
  lists: () => [...managerKeys.all, 'list'] as const,
  list: (clientId?: string, search?: string) => [...managerKeys.lists(), { clientId, search }] as const,
  details: () => [...managerKeys.all, 'detail'] as const,
  detail: (id: string) => [...managerKeys.details(), id] as const,
};

// Fetch managers with optional filters
export function useManagers(clientId?: string, search?: string) {
  return useQuery({
    queryKey: managerKeys.list(clientId, search),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (clientId) params.client_id = clientId;
      if (search) params.search = search;

      const response = await apiClient.get<ManagersResponse>('/managers', {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.managers;
    },
  });
}

// Fetch single manager by ID
export function useManager(id: string | undefined) {
  return useQuery({
    queryKey: managerKeys.detail(id!),
    queryFn: async () => {
      const response = await apiClient.get<ManagerDetailResponse>(`/managers/${id}`);
      return response.manager;
    },
    enabled: !!id,
  });
}

// Create manager mutation
export function useCreateManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: ManagerInput) => {
      const response = await apiClient.post<ManagerDetailResponse>('/managers', data);
      return response.manager;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: managerKeys.lists() });
      // Also invalidate the client detail to refresh the managers list
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(data.clientId) });
      toast({
        title: 'Success',
        description: 'Manager created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create manager',
        variant: 'destructive',
      });
    },
  });
}

// Update manager mutation
export function useUpdateManager(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Partial<ManagerInput>) => {
      const response = await apiClient.put<ManagerDetailResponse>(`/managers/${id}`, data);
      return response.manager;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: managerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: managerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(data.clientId) });
      toast({
        title: 'Success',
        description: 'Manager updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update manager',
        variant: 'destructive',
      });
    },
  });
}

// Delete manager mutation
export function useDeleteManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/managers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managerKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.details() });
      toast({
        title: 'Success',
        description: 'Manager deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete manager',
        variant: 'destructive',
      });
    },
  });
}
